import { useEffect, useRef, useState } from "react";

/* ============ MAZES (rotate per level) ============ */
const MAZE_DEFS = [
  {
    name: "NEON GRID",
    stroke: "#3E63FF",
    fill: "#101A3F",
    raw: [
      "###############",
      "#o....#.#....o#",
      "#.###.#.#.###.#",
      "#.............#",
      "#.#.#######.#.#",
      "#.#.........#.#",
      "#.#.###.###.#.#",
      "#...#..G..#...#",
      "#.#.#.###.#.#.#",
      "..#.........#..",
      "#.#.#######.#.#",
      "#.#.o.....o.#.#",
      "#.#.###.###.#.#",
      "#...#..P..#...#",
      "#.###.#.#.###.#",
      "#.............#",
      "###############",
    ],
  },
  {
    name: "MAGMA VAULT",
    stroke: "#FF5D8F",
    fill: "#3A1030",
    raw: [
      "###############",
      "#......#......#",
      "#o####.#.####o#",
      "#.#.........#.#",
      "#.#.##.#.##.#.#",
      "#......#......#",
      "#.###.###.###.#",
      "#.#....G....#.#",
      "#.#.##.#.##.#.#",
      "...#.......#...",
      "##.#.#####.#.##",
      "#..#...#...#..#",
      "#o##.#.#.#.##o#",
      "#....#.P.#....#",
      "#.##.#.#.#.##.#",
      "#.............#",
      "###############",
    ],
  },
  {
    name: "JADE DEPTHS",
    stroke: "#2ED9C3",
    fill: "#0B2E2A",
    raw: [
      "###############",
      "#o.....#.....o#",
      "#.#.##.#.##.#.#",
      "#.#.........#.#",
      "#...###.###...#",
      "##.#...#...#.##",
      "#..#.#####.#..#",
      "#.##.#.G.#.##.#",
      "#.##.#.#.#.##.#",
      "...............",
      "#.##.#.#.#.##.#",
      "#....#...#....#",
      "#.####.#.####.#",
      "#o.....P.....o#",
      "#.###.#.#.###.#",
      "#.............#",
      "###############",
    ],
  },
];

const W = 15;
const H = 17;
const TILE = 26;

const DIRS = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};
const DIR_LIST = [DIRS.up, DIRS.left, DIRS.down, DIRS.right];
const GHOST_COLORS = ["#FF5D5D", "#4DE1E1", "#FF9DE2", "#B78CFF"];

/* Process a raw maze into spawns + pellet map */
function processMaze(def) {
  let ghost = { x: 7, y: 7 };
  let player = { x: 7, y: 13 };
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (def.raw[y][x] === "G") ghost = { x, y };
      if (def.raw[y][x] === "P") player = { x, y };
    }
  }
  // up to 4 spawn tiles around the ghost anchor, on open tiles in its row
  const spawns = [];
  const offsets = [0, -1, 1, -2, 2, -3, 3];
  for (const o of offsets) {
    const x = ghost.x + o;
    if (x > 0 && x < W - 1 && def.raw[ghost.y][x] !== "#") {
      spawns.push({ x, y: ghost.y });
      if (spawns.length === 4) break;
    }
  }
  while (spawns.length < 4) spawns.push({ ...ghost });
  return { ...def, ghost, player, spawns };
}
const MAZES = MAZE_DEFS.map(processMaze);

function buildPellets(raw) {
  const p = new Map();
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const c = raw[y][x];
      if (c === ".") p.set(y * W + x, 1);
      if (c === "o") p.set(y * W + x, 2);
    }
  }
  return p;
}

/* ============ COMPONENT ============ */
export default function MunchMaze() {
  const canvasRef = useRef(null);
  const wallLayerRef = useRef(null);
  const stateRef = useRef(null);
  const rafRef = useRef(0);

  const [score, setScore] = useState(0);
  const [best, setBest] = useState(0);
  const [lives, setLives] = useState(3);
  const [level, setLevel] = useState(1);
  const [mazeName, setMazeName] = useState(MAZES[0].name);
  const [status, setStatus] = useState("ready"); // ready | playing | paused | gameover | clear

  function isWall(x, y) {
    const s = stateRef.current;
    const raw = s ? s.maze.raw : MAZES[0].raw;
    const cx = ((Math.round(x) % W) + W) % W;
    const cy = Math.round(y);
    if (cy < 0 || cy >= H) return true;
    return raw[cy][cx] === "#";
  }

  /* ---------- game state factory ---------- */
  function freshEntities(lvl, maze) {
    const speedMul = 1 + (lvl - 1) * 0.07;
    const now = performance.now() / 1000;
    return {
      player: {
        x: maze.player.x,
        y: maze.player.y,
        dir: null,
        queued: DIRS.left,
        speed: 5.2 * speedMul,
        mouth: 0,
      },
      ghosts: maze.spawns.map((sp, i) => ({
        x: sp.x,
        y: sp.y,
        dir: DIRS.up,
        speed: 4.4 * speedMul,
        color: GHOST_COLORS[i],
        persona: i,
        frightened: false,
        eyes: false,
        waitUntil: now + i * 1.6,
        spawn: sp,
        home: maze.ghost,
      })),
      frightUntil: 0,
      ghostCombo: 0,
    };
  }

  function loadMaze(lvl) {
    const maze = MAZES[(lvl - 1) % MAZES.length];
    setMazeName(maze.name);
    buildWallLayer(maze);
    return maze;
  }

  function initGame() {
    const maze = loadMaze(1);
    stateRef.current = {
      maze,
      pellets: buildPellets(maze.raw),
      ...freshEntities(1, maze),
      score: 0,
      lives: 3,
      level: 1,
      lastT: performance.now() / 1000,
      running: false,
    };
    setScore(0);
    setLives(3);
    setLevel(1);
    setStatus("ready");
  }

  /* ---------- movement ---------- */
  function stepEntity(e, dt, decide) {
    let remaining = e.speed * dt;
    let guard = 0;
    while (remaining > 1e-6 && guard++ < 8) {
      const cx = Math.round(e.x);
      const cy = Math.round(e.y);
      const atCenter =
        Math.abs(e.x - cx) < 1e-6 && Math.abs(e.y - cy) < 1e-6;
      if (atCenter) {
        const d = decide(e, cx, cy);
        if (!d || isWall(cx + d.x, cy + d.y)) break;
        e.dir = d;
        const move = Math.min(remaining, 1);
        e.x = cx + d.x * move;
        e.y = cy + d.y * move;
        remaining -= move;
      } else {
        if (!e.dir) break;
        const isX = e.dir.x !== 0;
        const cur = isX ? e.x : e.y;
        const sgn = isX ? e.dir.x : e.dir.y;
        const nextC =
          sgn > 0 ? Math.floor(cur + 1e-6) + 1 : Math.ceil(cur - 1e-6) - 1;
        const toNext = Math.abs(nextC - cur);
        const move = Math.min(remaining, toNext);
        if (isX) e.x += sgn * move;
        else e.y += sgn * move;
        remaining -= move;
        if (move >= toNext - 1e-9) {
          if (isX) e.x = nextC;
          else e.y = nextC;
        }
      }
      // tunnel wrap (any row open at the edges)
      if (e.x < -0.5) e.x += W;
      if (e.x > W - 0.5) e.x -= W;
    }
  }

  function playerDecide(e) {
    const cx = Math.round(e.x);
    const cy = Math.round(e.y);
    if (e.queued && !isWall(cx + e.queued.x, cy + e.queued.y)) return e.queued;
    if (e.dir && !isWall(cx + e.dir.x, cy + e.dir.y)) return e.dir;
    return null;
  }

  function ghostDecide(g, cx, cy) {
    const s = stateRef.current;
    const now = performance.now() / 1000;
    if (!g.eyes && now < g.waitUntil) return null;
    const rev = g.dir ? { x: -g.dir.x, y: -g.dir.y } : null;
    let options = DIR_LIST.filter(
      (d) =>
        !isWall(cx + d.x, cy + d.y) &&
        !(rev && d.x === rev.x && d.y === rev.y)
    );
    if (options.length === 0) options = rev && !isWall(cx + rev.x, cy + rev.y) ? [rev] : [];
    if (options.length === 0) return null;

    // eyes: race straight home
    if (g.eyes) {
      return closestTo(options, cx, cy, g.home.x, g.home.y);
    }
    if (g.frightened) {
      return options[Math.floor(Math.random() * options.length)];
    }
    const p = s.player;
    let tx = p.x;
    let ty = p.y;
    if (g.persona === 1 && p.dir) {
      tx = p.x + p.dir.x * 3;
      ty = p.y + p.dir.y * 3;
    } else if (g.persona === 2) {
      return options[Math.floor(Math.random() * options.length)];
    } else if (g.persona === 3) {
      const dist = Math.hypot(p.x - g.x, p.y - g.y);
      if (dist < 4) {
        tx = 1;
        ty = H - 2;
      }
    }
    return closestTo(options, cx, cy, tx, ty);
  }

  function closestTo(options, cx, cy, tx, ty) {
    let bestD = options[0];
    let bestScore = Infinity;
    for (const d of options) {
      const nx = cx + d.x;
      const ny = cy + d.y;
      const sc = (nx - tx) * (nx - tx) + (ny - ty) * (ny - ty);
      if (sc < bestScore) {
        bestScore = sc;
        bestD = d;
      }
    }
    return bestD;
  }

  /* ---------- per-frame logic ---------- */
  function update(dt) {
    const s = stateRef.current;
    const now = performance.now() / 1000;

    stepEntity(s.player, dt, playerDecide);
    s.player.mouth += dt * 10;

    // eat pellets
    const pkey = Math.round(s.player.y) * W + Math.round(s.player.x);
    const pv = s.pellets.get(pkey);
    if (pv) {
      s.pellets.delete(pkey);
      s.score += pv === 2 ? 50 : 10;
      if (pv === 2) {
        s.frightUntil = now + Math.max(3, 6.5 - s.level * 0.4);
        s.ghostCombo = 0;
        for (const g of s.ghosts) {
          if (!g.eyes && now >= g.waitUntil) {
            g.frightened = true;
            if (g.dir) g.dir = { x: -g.dir.x, y: -g.dir.y };
          }
        }
      }
      setScore(s.score);
    }

    if (now > s.frightUntil) {
      for (const g of s.ghosts) g.frightened = false;
    }

    // ghosts
    const base = 4.4 * (1 + (s.level - 1) * 0.07);
    for (const g of s.ghosts) {
      g.speed = g.eyes ? base * 1.7 : g.frightened ? base * 0.55 : base;
      stepEntity(g, dt, (e, cx, cy) => ghostDecide(e, cx, cy));

      // eyes reached home -> regenerate
      if (g.eyes && Math.hypot(g.x - g.home.x, g.y - g.home.y) < 0.25) {
        g.eyes = false;
        g.x = g.spawn.x;
        g.y = g.spawn.y;
        g.dir = DIRS.up;
        g.waitUntil = now + 1.2;
      }
    }

    // collisions
    for (const g of s.ghosts) {
      if (g.eyes || now < g.waitUntil) continue;
      const d = Math.hypot(g.x - s.player.x, g.y - s.player.y);
      if (d < 0.7) {
        if (g.frightened) {
          s.ghostCombo += 1;
          s.score += 200 * Math.pow(2, s.ghostCombo - 1);
          setScore(s.score);
          g.frightened = false;
          g.eyes = true; // travel back to the house
        } else {
          loseLife();
          return;
        }
      }
    }

    if (s.pellets.size === 0) {
      s.running = false;
      setStatus("clear");
    }
  }

  function loseLife() {
    const s = stateRef.current;
    s.lives -= 1;
    setLives(s.lives);
    s.running = false;
    if (s.lives <= 0) {
      setBest((b) => Math.max(b, s.score));
      setStatus("gameover");
    } else {
      resetPositions();
      setStatus("ready");
    }
  }

  function resetPositions() {
    const s = stateRef.current;
    const fresh = freshEntities(s.level, s.maze);
    s.player = fresh.player;
    s.ghosts = fresh.ghosts;
    s.frightUntil = 0;
  }

  function nextLevel() {
    const s = stateRef.current;
    s.level += 1;
    setLevel(s.level);
    s.maze = loadMaze(s.level);
    s.pellets = buildPellets(s.maze.raw);
    resetPositions();
    setStatus("ready");
  }

  /* ---------- drawing ---------- */
  function buildWallLayer(maze) {
    const c = document.createElement("canvas");
    c.width = W * TILE;
    c.height = H * TILE;
    const g = c.getContext("2d");
    g.fillStyle = "#070B18";
    g.fillRect(0, 0, c.width, c.height);
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        if (maze.raw[y][x] !== "#") continue;
        const px = x * TILE;
        const py = y * TILE;
        g.fillStyle = maze.fill;
        g.strokeStyle = maze.stroke;
        g.lineWidth = 2;
        roundRect(g, px + 3, py + 3, TILE - 6, TILE - 6, 6);
        g.fill();
        g.stroke();
      }
    }
    wallLayerRef.current = c;
  }

  function roundRect(g, x, y, w, h, r) {
    g.beginPath();
    g.moveTo(x + r, y);
    g.arcTo(x + w, y, x + w, y + h, r);
    g.arcTo(x + w, y + h, x, y + h, r);
    g.arcTo(x, y + h, x, y, r);
    g.arcTo(x, y, x + w, y, r);
    g.closePath();
  }

  function drawEyes(g, gx, gy, dirX, dirY) {
    g.fillStyle = "#FFFFFF";
    g.beginPath();
    g.arc(gx - 5, gy - 4, 4, 0, Math.PI * 2);
    g.arc(gx + 5, gy - 4, 4, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = "#1B1F3A";
    g.beginPath();
    g.arc(gx - 5 + dirX, gy - 4 + dirY, 2, 0, Math.PI * 2);
    g.arc(gx + 5 + dirX, gy - 4 + dirY, 2, 0, Math.PI * 2);
    g.fill();
  }

  function draw() {
    const canvas = canvasRef.current;
    const s = stateRef.current;
    if (!canvas || !s || !wallLayerRef.current) return;
    const g = canvas.getContext("2d");
    const now = performance.now() / 1000;

    g.drawImage(wallLayerRef.current, 0, 0);

    // pellets
    for (const [key, val] of s.pellets) {
      const x = (key % W) * TILE + TILE / 2;
      const y = Math.floor(key / W) * TILE + TILE / 2;
      g.fillStyle = "#FFC94A";
      g.beginPath();
      if (val === 2) {
        const r = 5.5 + Math.sin(now * 6) * 1.5;
        g.arc(x, y, r, 0, Math.PI * 2);
      } else {
        g.arc(x, y, 2.6, 0, Math.PI * 2);
      }
      g.fill();
    }

    // player
    const p = s.player;
    const px = p.x * TILE + TILE / 2;
    const py = p.y * TILE + TILE / 2;
    const open = (Math.sin(p.mouth) * 0.5 + 0.5) * 0.9 + 0.15;
    let ang = 0;
    if (p.dir) ang = Math.atan2(p.dir.y, p.dir.x);
    g.save();
    g.translate(px, py);
    g.rotate(ang);
    g.fillStyle = "#FFD23F";
    g.beginPath();
    g.arc(0, 0, TILE / 2 - 3, open * 0.5, Math.PI * 2 - open * 0.5);
    g.lineTo(0, 0);
    g.closePath();
    g.fill();
    g.restore();

    // ghosts
    const flash =
      s.frightUntil - now < 1.8 && Math.floor(now * 8) % 2 === 0;
    for (const gh of s.ghosts) {
      const gx = gh.x * TILE + TILE / 2;
      const gy = gh.y * TILE + TILE / 2;
      const r = TILE / 2 - 3;
      const ex = gh.dir ? gh.dir.x * 2.5 : 0;
      const ey = gh.dir ? gh.dir.y * 2.5 : 0;

      if (gh.eyes) {
        drawEyes(g, gx, gy, ex, ey);
        continue;
      }

      const waiting = now < gh.waitUntil;
      g.save();
      g.globalAlpha = waiting ? 0.45 : 1;
      g.fillStyle = gh.frightened
        ? flash
          ? "#EDEFFB"
          : "#4753FF"
        : gh.color;
      g.beginPath();
      g.arc(gx, gy - 2, r, Math.PI, 0);
      const bottom = gy - 2 + r;
      g.lineTo(gx + r, bottom);
      for (let i = 3; i >= 0; i--) {
        const sx = gx - r + (i / 4) * 2 * r;
        g.lineTo(sx + r / 4, bottom - 4);
        g.lineTo(sx, bottom);
      }
      g.closePath();
      g.fill();
      drawEyes(g, gx, gy, ex, ey);
      g.restore();
    }
  }

  /* ---------- loop ---------- */
  function loop() {
    const s = stateRef.current;
    if (!s) return;
    const now = performance.now() / 1000;
    let dt = now - s.lastT;
    s.lastT = now;
    if (dt > 0.05) dt = 0.05;
    if (s.running) update(dt);
    draw();
    rafRef.current = requestAnimationFrame(loop);
  }

  /* ---------- controls ---------- */
  function queueDir(name) {
    const s = stateRef.current;
    if (!s) return;
    s.player.queued = DIRS[name];
    if (!s.running) startIfReady();
  }

  function startIfReady() {
    const s = stateRef.current;
    setStatus((st) => {
      if (st === "ready" || st === "paused") {
        const now = performance.now() / 1000;
        s.lastT = now;
        s.running = true;
        return "playing";
      }
      if (st === "clear") {
        nextLevel();
        return "ready";
      }
      if (st === "gameover") {
        initGame();
        return "ready";
      }
      return st;
    });
  }

  function togglePause() {
    const s = stateRef.current;
    setStatus((st) => {
      if (st === "playing") {
        s.running = false;
        return "paused";
      }
      if (st === "paused") {
        s.running = true;
        s.lastT = performance.now() / 1000;
        return "playing";
      }
      return st;
    });
  }

  /* ---------- effects ---------- */
  useEffect(() => {
    initGame();

    const canvas = canvasRef.current;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = W * TILE * dpr;
    canvas.height = H * TILE * dpr;
    canvas.getContext("2d").scale(dpr, dpr);

    rafRef.current = requestAnimationFrame(loop);

    const onKey = (e) => {
      const map = {
        ArrowUp: "up",
        ArrowDown: "down",
        ArrowLeft: "left",
        ArrowRight: "right",
        w: "up",
        s: "down",
        a: "left",
        d: "right",
      };
      if (map[e.key]) {
        e.preventDefault();
        queueDir(map[e.key]);
      } else if (e.key === " ") {
        e.preventDefault();
        togglePause();
      } else if (e.key === "Enter") {
        startIfReady();
      }
    };
    window.addEventListener("keydown", onKey);

    const onVis = () => {
      if (document.hidden) {
        const s = stateRef.current;
        if (s && s.running) {
          s.running = false;
          setStatus("paused");
        }
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- touch (swipe) ---------- */
  const touchRef = useRef(null);
  function onTouchStart(e) {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, used: false };
  }
  function onTouchMove(e) {
    const start = touchRef.current;
    if (!start || start.used) return;
    const t = e.touches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < 22 && Math.abs(dy) < 22) return;
    start.used = true;
    if (Math.abs(dx) > Math.abs(dy)) queueDir(dx > 0 ? "right" : "left");
    else queueDir(dy > 0 ? "down" : "up");
  }
  function onTouchEnd() {
    const start = touchRef.current;
    if (start && !start.used) startIfReady();
    touchRef.current = null;
  }

  /* ---------- overlay ---------- */
  const overlay =
    status === "ready"
      ? { big: "READY!", small: mazeName + " · Swipe or tap to start" }
      : status === "paused"
      ? { big: "PAUSED", small: "Tap to resume" }
      : status === "clear"
      ? {
          big: "MAZE CLEARED!",
          small:
            "Next: " +
            MAZES[level % MAZES.length].name +
            " · Tap for level " +
            (level + 1),
        }
      : status === "gameover"
      ? { big: "GAME OVER", small: "Tap to play again" }
      : null;

  const pad = (btnDir, label) => (
    <button
      key={btnDir}
      onPointerDown={(e) => {
        e.preventDefault();
        queueDir(btnDir);
      }}
      style={{
        gridArea: btnDir,
        width: 58,
        height: 58,
        borderRadius: 14,
        border: "1px solid #2A3560",
        background: "#131B3B",
        color: "#8FA3FF",
        fontSize: 20,
        fontFamily: "inherit",
        touchAction: "manipulation",
        userSelect: "none",
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(120% 90% at 50% 0%, #101736 0%, #05070F 70%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        fontFamily: "'Press Start 2P', 'Courier New', monospace",
        color: "#EDEFFB",
        paddingBottom: 24,
        touchAction: "manipulation",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap');
        button:active { transform: scale(0.94); }
        button:focus-visible { outline: 2px solid #FFD23F; outline-offset: 2px; }
      `}</style>

      <div style={{ textAlign: "center", padding: "18px 12px 10px" }}>
        <div
          style={{
            fontSize: 22,
            letterSpacing: 2,
            color: "#FFD23F",
            textShadow: "0 0 14px rgba(255,210,63,0.55)",
          }}
        >
          MUNCH MAZE
        </div>
        <div style={{ fontSize: 8, marginTop: 8, color: "#6B7BC4" }}>
          {mazeName}
        </div>
      </div>

      <div
        style={{
          width: "min(92vw, 400px)",
          display: "flex",
          justifyContent: "space-between",
          fontSize: 9,
          marginBottom: 8,
          color: "#9AA8E8",
        }}
      >
        <span>
          SCORE <span style={{ color: "#FFD23F" }}>{score}</span>
        </span>
        <span>
          BEST <span style={{ color: "#FFD23F" }}>{Math.max(best, score)}</span>
        </span>
        <span>
          LV <span style={{ color: "#FFD23F" }}>{level}</span>
        </span>
        <span style={{ color: "#FF5D5D" }}>
          {"\u2665".repeat(Math.max(lives, 0))}
        </span>
      </div>

      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={startIfReady}
        style={{
          position: "relative",
          width: "min(92vw, 400px)",
          borderRadius: 16,
          overflow: "hidden",
          border: "1px solid #263260",
          boxShadow:
            "0 0 0 1px #1B2450, 0 12px 40px rgba(0,0,0,0.6), inset 0 0 60px rgba(20,30,80,0.5)",
          touchAction: "none",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{ display: "block", width: "100%", height: "auto" }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "repeating-linear-gradient(0deg, rgba(0,0,0,0.16) 0px, rgba(0,0,0,0.16) 1px, transparent 1px, transparent 3px), radial-gradient(110% 110% at 50% 50%, transparent 60%, rgba(0,0,10,0.5) 100%)",
            mixBlendMode: "multiply",
          }}
        />
        {overlay && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 14,
              background: "rgba(5,7,15,0.62)",
              backdropFilter: "blur(2px)",
              padding: 12,
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: 18,
                color: status === "gameover" ? "#FF5D5D" : "#FFD23F",
                textShadow: "0 0 12px currentColor",
              }}
            >
              {overlay.big}
            </div>
            <div style={{ fontSize: 8, color: "#9AA8E8", lineHeight: 1.8 }}>
              {overlay.small}
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          marginTop: 16,
          display: "flex",
          alignItems: "center",
          gap: 28,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateAreas: `". up ." "left down right"`,
            gap: 8,
          }}
        >
          {pad("up", "\u25B2")}
          {pad("left", "\u25C0")}
          {pad("down", "\u25BC")}
          {pad("right", "\u25B6")}
        </div>
        <button
          onClick={togglePause}
          style={{
            width: 58,
            height: 58,
            borderRadius: "50%",
            border: "1px solid #4a3a10",
            background: "#2A2410",
            color: "#FFD23F",
            fontSize: 9,
            fontFamily: "inherit",
            touchAction: "manipulation",
          }}
        >
          {status === "paused" ? "GO" : "II"}
        </button>
      </div>

      <div style={{ fontSize: 7, color: "#4A5590", marginTop: 16 }}>
        SWIPE OR USE D-PAD · POWER PELLETS TURN THE TABLES
      </div>
    </div>
  );
}
